import { useState, useEffect } from 'react';
import { PLANBASE_CONFIG } from '../config';
import { getUserAuthHeaders } from '../utils/auth';

interface Bookmark {
  id: string;
  slug: string;
  collection_name: string;
  created_at: string;
}

interface UseBookmarksResult {
  bookmarks: Bookmark[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBookmarks(collectionName?: string): UseBookmarksResult {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem('planbase_token');

  const fetchBookmarks = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = new URL(`${PLANBASE_CONFIG.apiUrl}/sdk/bookmarks`);
      if (collectionName) {
        url.searchParams.set('collection', collectionName);
      }

      const response = await fetch(url.toString(), {
        headers: getUserAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        // The API returns bookmarks in data.data, not data.bookmarks
        setBookmarks(data.data || []);
      } else {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          setError(errorData.error || 'Failed to fetch bookmarks');
        } catch {
          setError(`HTTP ${response.status}: ${errorText}`);
        }
      }
    } catch (err) {
      setError('Failed to fetch bookmarks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookmarks();
  }, [token, collectionName]);

  return {
    bookmarks,
    loading,
    error,
    refetch: fetchBookmarks,
  };
}
